import React, { useCallback, useEffect, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import useIsMobile from 'hooks/useIsMobile';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import { checkUserCountry } from 'utils/locale';
import { flowTrialActive } from 'utils';
import { useRouter } from 'next/router';
import LoadingCircle from 'components/LoadingCircle';
import { useAuthUser } from 'features/auth/AuthUserContext';
import { withTranslations } from 'utils/withTranslations';
import useGeoData from 'hooks/useGeoData';
import { useTranslation } from 'next-i18next';
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics';
import { useInactivity } from 'hooks/useInactivity';
import * as spinWheel from 'utils/spinWheel';
import { UserTypeEnum } from 'enums';
import usePlan from 'hooks/usePlan';

const DynamicBaseLayout = dynamic(() => import('layouts/BaseLayout'));
const DynamicMainContent = dynamic(() => import('components/Pricing/MainContent'));

const schemeData = {
  '@context': 'http://schema.org/',
  '@type': 'Review',
  itemReviewed: {
    '@type': 'Organization',
    name: 'Mind the Graph',
  },
  reviewRating: {
    '@type': 'Rating',
    ratingValue: '5',
  },
  name: 'PhD Student at LMU / Germany',
  author: {
    '@type': 'Person',
    name: 'Susanne Fiedler',
  },
  reviewBody: 'Very easy to use and graphs are designed quite quickly!',
};

const schemeDataFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I use Mind the Graph for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. For as long as you want, Mind the Graph is freemium. No credit card information is required. However, keep in mind that the Beginner plan is a way to get to know the platform, informal communication with your peers, or educational environments.\n\nAs a free user, you must keep Mind the Graph\'s watermark in all cases and cite the platform at formal communications, publications, lectures, and presentations.\n\nTo use the platform without watermarks, get more creations and Creative Commons licenses for them, upgrade your plan... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between the annual and the monthly plan?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'As an annual subscriber, you get up to 47% off (almost 6 months for free!). Just a one-time fee for the entire year, renewed every 12 months.\n\nAs a monthly subscriber, payments are every month, with automatic renewal by default. However, you can cancel at any time and it will not cost you any extra fees... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I cancel a paid subscription anytime?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Upon cancellation, you will still be able to use Mind the Graph\'s platform as a premium user - until the end of your previously subscribed period*.\n\nAfter it expires, your plan will be downgraded to Beginner, and all your new creations must respect the free accounts license. Your prior creations will be blocked, but we\'ll keep them safe in case you ever want to come back as a premium user.\n\n*Except for Paypal payments, in which the subscription cancels immediately... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the benefits of a paid plan?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Extra features and resources! All paid plans offer unwatermarked icons and Creative Commons licenses for your creations. Also, you\'ll have access to premium customer support, requests for personalized illustrations, restoring deleted files, and more. Don\'t forget to compare the plans above!... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use Mind the Graph in my publications?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Sure! You can use Mind the Graph for graphical abstracts, scientific papers, presentations, classes, books, lectures, blogs, social media, and so on. However, keep in mind that free users must keep the watermark and cite Mind the Graph as the visual content resource. Premium users don\'t need to cite Mind the Graph (but we\'ll be happy if you do!)... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: "How can I use Mind the Graph's illustrations without the watermarks?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Just subscribe. All paid plans don\'t require the use of Mind the Graph\'s watermark. You can check them all above.... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'How does the citation license work?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The contents stored and created with <a href="https://mindthegraph.com/">mindthegraph.com</a> are available under a free culture Creative Commons license.\n\nFree users can publicly the infographics created at Mind the Graph\'s platform and use the illustrations available on the website. However, you must cite Mind the Graph, and the use of watermarks is mandatory.\n\nPremium subscribers are entitled to full rights to their creations and are recognized as authors, keeping its rights and ability to transfer authorial rights to third-party publishers without previous consult to Mind the Graph. So, if you\'re a paid user, you don\'t need to attribute credits to Mind the Graph (but we\'ll be happy if you do!)... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'What are the benefits of the ProTeams group plan?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The ProTeams plan is designed for academic and industry teams with a need for advanced and personalized resources. It has a limit of up to 11 users (which makes it cheaper than individual subscriptions), dedicated customer support, team collaboration, multiple simultaneous users, assistance from our design team for templates, slides presentations, brand patterns, discounts for graphical abstracts, and the possibility of confidentiality for on-demand illustrations. Not sure if that\'s what you need? Let us know and we\'ll be happy to help you... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I get the subscription cost reimbursed by my University?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'As Mind the Graph is a platform built to help scientists increase their impact and spread visual communication across scientific and educational environments, many universities provide financial assistance or reimbursement for our subscriptions. You must check with your lab head, professor, or director about the availability... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I access my invoices?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'If you ever need to download your invoices for any purpose (including reimbursement), you can easily access them through your profile page. Check the tutorial here... <a href="https://mindthegraph.com/pricing">Read more</a>',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to pay to request an illustration?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Mind the Graph is the world's largest scientifically accurate illustrations gallery. However, if you can't find an illustration that fits your needs, request it for free (if you're a paid subscriber). Check how to do it... <a href=\"https://mindthegraph.com/pricing\">Read more</a>",
      },
    },
  ],
};

export default function PricingPage() {
  const { user, isLoading: isLoadingUser, isAuthenticated } = useAuthUser();
  const router = useRouter();
  const isMobile = useIsMobile();
  const [isChina, setIsChina] = useState(null);
  const [isJapan, setIsJapan] = useState(null);
  const [isCountryCheckComplete, setIsCountryCheckComplete] = useState(false);
  const [userCountry, setUserCountry] = useState(null);
  const { geoData, isLoadingGeoData } = useGeoData();
  const { t } = useTranslation('pricing');
  const { i18n } = useTranslation();
  const { gaIdentifiers } = useGoogleAnalytics() || {};
  const [plan, , isLoadingPlan] = usePlan();

  const changeLanguage = (locale) => {
    i18n.changeLanguage(locale) && router.push(router.pathname, router.asPath, { locale });
  };

  /*const openSpinWheel = useCallback(() => {
    if (user && user.tp_user !== UserTypeEnum.TRIAL) return;
    setTimeout(() => {
      spinWheel.open({
        userCountry: user?.country_code || geoData?.country_code || 'US',
        clientId: gaIdentifiers?.client_id,
        sessionId: gaIdentifiers?.session_id,
      });
    }, 200);
  }, [user, geoData, gaIdentifiers]);
  useInactivity(20000, openSpinWheel);*/

  useEffect(() => {
    if (isAuthenticated && !isLoadingPlan && plan) {
      router.push('/my-account/overview/');
    }
  }, [isAuthenticated, isLoadingPlan, plan]);

  useEffect(() => {
    async function getUserIp() {
      setIsChina(await checkUserCountry.isChina(user));
      setIsJapan(await checkUserCountry.isJapan(user));
      setIsCountryCheckComplete(true);
    }

    if (isLoadingUser || isLoadingGeoData) return;

    const userCountry = isAuthenticated ? user.country_code : geoData.country_code;
    setUserCountry(userCountry);
    getUserIp();
  }, [user, isLoadingUser, isLoadingGeoData, isAuthenticated, user?.country_code]);

  useEffect(() => {
    if (isChina !== null && isJapan !== null) {
      if (!(isChina || isJapan) && flowTrialActive) {
        router.push('/pricing-alt');
      }
    }
    // set proper locale
    (router.locale !== 'en' && !isChina && !isJapan && changeLanguage('en')) ||
      (isChina && router.locale !== 'zh' && changeLanguage('zh')) ||
      (isJapan && router.locale !== 'ja' && changeLanguage('ja'));
  }, [isChina, isJapan, flowTrialActive]);

  if (!isCountryCheckComplete) {
    return <LoadingCircle sizeProps={70} />;
  }

  return (
    <DynamicBaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      typeBtn="buttonSecondary"
      headerWhite={true}
      hasPadding={false}
      userCountry={userCountry}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemeData) }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemeDataFaq) }}
        />

        <title>{t('meta.page_title')}</title>

        <meta key="og:title" name="og:title" property="og:title" content={t('meta.og.title')} />

        <meta key="description" name="description" content={t('meta.description')} />

        <meta
          key="og:description"
          name="og:description"
          property="og:description"
          content={t('meta.og.description')}
        />
      </Head>

      <DynamicMainContent userCountry={userCountry} />
    </DynamicBaseLayout>
  );
}
export const getStaticProps = withTranslations(['pricing', 'common']);
